import { Fragment, type ReactNode, createElement } from 'react';
import { useIntl } from 'react-intl';

import AMRAPIcon from 'app/ui/components/atoms/icons/section-formats/amrap/icon';
import ChipperIcon from 'app/ui/components/atoms/icons/section-formats/chipper/icon';
import CoupletIcon from 'app/ui/components/atoms/icons/section-formats/couplet/icon';
import IGOUYGOIcon from 'app/ui/components/atoms/icons/section-formats/i-go-you-go/icon';
import IntervalsIcon from 'app/ui/components/atoms/icons/section-formats/intervals/icon';
import PartnerWorkoutIcon from 'app/ui/components/atoms/icons/section-formats/partner/icon';
import TabataIcon from 'app/ui/components/atoms/icons/section-formats/tabata/icon';
import DoubleTabataIcon from 'app/ui/components/atoms/icons/section-formats/tabata-double/icon';
import TO2MIcon from 'app/ui/components/atoms/icons/section-formats/to2m/icon';
import TO3MIcon from 'app/ui/components/atoms/icons/section-formats/to3m/icon';
import TOTMIcon from 'app/ui/components/atoms/icons/section-formats/totm/icon';
import TripletIcon from 'app/ui/components/atoms/icons/section-formats/triplet/icon';
import RotationalIcon from 'app/ui/components/atoms/icons/section-formats/rotation/icon';

import { LessonSectionFormat } from 'app/types/graphql';

export enum OtherSectionIcons {
  ROTATIONAL = 'ROTATIONAL',
}

type AvailableSectionFormats = LessonSectionFormat | OtherSectionIcons;

type SectionFormatIconDetails = {
  icon: ReactNode;
  name: string;
  description: string;
};

const useSectionFormatDetails = (format: AvailableSectionFormats, color?: string): SectionFormatIconDetails => {
  const intl = useIntl();

  switch (format) {
    case LessonSectionFormat.AMRAP:
      return {
        icon: createElement(AMRAPIcon, { color }),
        name: intl.formatMessage({ id: 'sectionFormats.amrap.name' }),
        description: intl.formatMessage({ id: 'sectionFormats.amrap.description' }),
      };
    case LessonSectionFormat.CHIPPER:
      return {
        icon: createElement(ChipperIcon, { color }),
        name: intl.formatMessage({ id: 'sectionFormats.chipper.name' }),
        description: intl.formatMessage({ id: 'sectionFormats.chipper.description' }),
      };
    case LessonSectionFormat.COUPLET:
      return {
        icon: createElement(CoupletIcon, { color }),
        name: intl.formatMessage({ id: 'sectionFormats.couplet.name' }),
        description: intl.formatMessage({ id: 'sectionFormats.couplet.description' }),
      };
    case LessonSectionFormat.I_GO_YOU_GO:
      return {
        icon: createElement(IGOUYGOIcon, { color }),
        name: intl.formatMessage({ id: 'sectionFormats.iGoYouGo.name' }),
        description: intl.formatMessage({ id: 'sectionFormats.iGoYouGo.description' }),
      };
    case LessonSectionFormat.INTERVALS:
      return {
        icon: createElement(IntervalsIcon, { color }),
        name: intl.formatMessage({ id: 'sectionFormats.intervals.name' }),
        description: intl.formatMessage({ id: 'sectionFormats.intervals.description' }),
      };
    case LessonSectionFormat.PARTNER_WORKOUT:
      return {
        icon: createElement(PartnerWorkoutIcon, { color }),
        name: intl.formatMessage({ id: 'sectionFormats.partnerWorkout.name' }),
        description: intl.formatMessage({ id: 'sectionFormats.partnerWorkout.description' }),
      };
    case LessonSectionFormat.PARTNER_CHIPPER:
      return {
        icon: createElement(
          'div',
          { style: { display: 'flex', gap: '0.25rem' } },
          createElement(PartnerWorkoutIcon, { color }),
          createElement(ChipperIcon, { color }),
        ),
        name: intl.formatMessage({ id: 'sectionFormats.partnerChipper.name' }),
        description: intl.formatMessage({ id: 'sectionFormats.partnerChipper.description' }),
      };
    case LessonSectionFormat.TABATA:
      return {
        icon: createElement(TabataIcon, { color }),
        name: intl.formatMessage({ id: 'sectionFormats.tabata.name' }),
        description: intl.formatMessage({ id: 'sectionFormats.tabata.description' }),
      };
    case LessonSectionFormat.DOUBLE_TABATA:
      return {
        icon: createElement(DoubleTabataIcon, { color }),
        name: intl.formatMessage({ id: 'sectionFormats.doubleTabata.name' }),
        description: intl.formatMessage({ id: 'sectionFormats.doubleTabata.description' }),
      };
    case LessonSectionFormat.TO2M:
      return {
        icon: createElement(TO2MIcon, { color }),
        name: intl.formatMessage({ id: 'sectionFormats.to2m.name' }),
        description: intl.formatMessage({ id: 'sectionFormats.to2m.description' }),
      };
    case LessonSectionFormat.TO3M:
      return {
        icon: createElement(TO3MIcon, { color }),
        name: intl.formatMessage({ id: 'sectionFormats.to3m.name' }),
        description: intl.formatMessage({ id: 'sectionFormats.to3m.description' }),
      };
    case LessonSectionFormat.TOTM:
      return {
        icon: createElement(TOTMIcon, { color }),
        name: intl.formatMessage({ id: 'sectionFormats.totm.name' }),
        description: intl.formatMessage({ id: 'sectionFormats.totm.description' }),
      };
    case LessonSectionFormat.TRIPLET:
      return {
        icon: createElement(TripletIcon, { color }),
        name: intl.formatMessage({ id: 'sectionFormats.triplet.name' }),
        description: intl.formatMessage({ id: 'sectionFormats.triplet.description' }),
      };
    case OtherSectionIcons.ROTATIONAL:
      return {
        icon: createElement(RotationalIcon, { color }),
        name: intl.formatMessage({ id: 'sectionFormats.rotational.name' }),
        description: intl.formatMessage({ id: 'sectionFormats.rotational.description' }),
      };
    default:
      return {
        icon: createElement(Fragment),
        name: '',
        description: '',
      };
  }
};

export default useSectionFormatDetails;
